// Timeline-window state hook.
//
// Owns the {mode, win, zoomHours, nowMs} tuple that drives the renderable
// window and the relative/absolute range control. Mutations:
//
//   - `setRelative(hours)` — back to "[NOW - hours, NOW]".
//   - `setAbsolute(start, end)` — fix a concrete window (drag-select, type-
//     in, ±10 min, focusWindow). Auto-flips mode to absolute.
//   - `zoom(factor, anchorFrac)` — anchor under cursor; clamps to MIN/MAX.
//   - `focusOn(clipStart, clipDur)` — frame a loaded clip.
//
// `nowMs` is updated by the container (from /api/health or Date.now()
// fallback) — not derived inside the hook so tests stay deterministic.

import { useCallback, useMemo, useState } from "react";

import {
  type AbsoluteWindow,
  type RangeMode,
  clampWindow,
  focusWindowAround,
  resolveWindow,
  zoomBy as zoomByFn,
} from "../helpers/window";

export interface TimelineWindowApi {
  mode: RangeMode;
  zoomHours: number;
  /** Concrete window the timeline renders. */
  win: AbsoluteWindow;
  /** Whether `win` mirrors a relative preset (used to highlight 1h/6h/24h/3d). */
  presetMatch: number | null;

  setRelative(hours: number): void;
  setAbsolute(start: number, end: number): void;
  flipMode(mode: RangeMode): void;
  zoom(factor: number, anchorFrac: number): void;
  focusOn(clipStartMs: number, clipDurMs: number): void;
}

const PRESETS_HOURS: readonly number[] = [1, 6, 24, 72];

function deriveWin(
  mode: RangeMode,
  storedWin: AbsoluteWindow | null,
  zoomHours: number,
  nowMs: number,
): AbsoluteWindow {
  return resolveWindow({ mode, win: storedWin, zoomHours, nowMs });
}

export function useTimelineWindow(nowMs: number): TimelineWindowApi {
  const [mode, setMode] = useState<RangeMode>("relative");
  const [zoomHours, setZoomHours] = useState<number>(24);
  const [storedWin, setStoredWin] = useState<AbsoluteWindow | null>(null);

  const win = useMemo(
    () => deriveWin(mode, storedWin, zoomHours, nowMs),
    [mode, storedWin, zoomHours, nowMs],
  );

  /**
   * Highlight the matching relative preset only when:
   *   - we're actually in relative mode, AND
   *   - the window-end is anchored at NOW (within a tick), AND
   *   - the span matches one of the presets (within a second).
   */
  const presetMatch = useMemo<number | null>(() => {
    if (mode !== "relative") return null;
    if (Math.abs(win.end - nowMs) >= 60_000) return null;
    const span = win.end - win.start;
    for (const h of PRESETS_HOURS) {
      if (Math.abs(span - h * 3_600_000) < 1_000) return h;
    }
    return null;
  }, [mode, win, nowMs]);

  const setRelative = useCallback((hours: number) => {
    setMode("relative");
    setZoomHours(hours);
    setStoredWin(null);
  }, []);

  const setAbsolute = useCallback((start: number, end: number) => {
    if (end <= start) return;
    setMode("absolute");
    setStoredWin(clampWindow(start, end));
  }, []);

  const flipMode = useCallback(
    (next: RangeMode) => {
      if (next === mode) return;
      if (next === "relative") {
        setMode("relative");
        setStoredWin(null);
      } else {
        // Capture the live window so absolute mode freezes exactly what's
        // on screen rather than snapping to a preset.
        setStoredWin(win);
        setMode("absolute");
      }
    },
    [mode, win],
  );

  const zoom = useCallback(
    (factor: number, anchorFrac: number) => {
      const z = zoomByFn(win, factor, anchorFrac);
      setMode("absolute");
      setStoredWin(z);
    },
    [win],
  );

  const focusOn = useCallback((clipStartMs: number, clipDurMs: number) => {
    const w = focusWindowAround(clipStartMs, clipDurMs);
    setMode("absolute");
    setStoredWin(w);
  }, []);

  return {
    mode,
    zoomHours,
    win,
    presetMatch,
    setRelative,
    setAbsolute,
    flipMode,
    zoom,
    focusOn,
  };
}
